import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, ExternalLink, Loader2, Plug, RefreshCw } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { z } from 'zod';
import { QueryKey } from '@/Config/api/queryKey';
import { Button, IconButton } from '@/Domain/components/Shared/Button/Button';
import { Input } from '@/Domain/components/Shared/Input/Input';
import { Label } from '@/Domain/components/Shared/Label/Label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/Domain/components/Shared/Select/Select';
import { Separator } from '@/Domain/components/Shared/Separator/Separator';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/Domain/components/Shared/Sheet/sheet';
import { Switch } from '@/Domain/components/Shared/Switch/Switch';
import CloseIcon from '@/Domain/components/icons/Close.icon';
import { HubSpotIcon } from '@/Domain/components/icons/HubSpot.icon';

const HUBSPOT_NS_PREFIX = 'integration:hubspot';
const HUBSPOT_INTEGRATION_NAME = 'hubspot' as const;

const HUBSPOT_QUERY_KEY = [QueryKey.integration.get, HUBSPOT_INTEGRATION_NAME] as const;
const HUBSPOT_TOKEN_STATUS_QUERY_KEY = [QueryKey.integration.hubspotTokenStatus] as const;
const HUBSPOT_PIPELINES_QUERY_KEY = [QueryKey.integration.hubspotPipelines] as const;
const HUBSPOT_WHATSAPP_TEMPLATE_QUERY_KEY = [QueryKey.integration.hubspotWhatsappTemplate] as const;
const HUBSPOT_WHATSAPP_TEMPLATES_LIST_QUERY_KEY = [QueryKey.whatsappTemplates.list] as const;
const AUTO_CLOSE_QUERY_KEY = [QueryKey.integration.autoCloseMinutes] as const;

const POPUP_POLL_INTERVAL_MS = 500;

const AUTO_CLOSE_VALUES = [null, 5, 10, 30] as const;
type AutoCloseValue = (typeof AUTO_CLOSE_VALUES)[number];

// Radix Select can't bind to the string "null"; use a sentinel for "never close".
const AUTO_CLOSE_NEVER_SENTINEL = '__never__';
// Sentinel used to represent "no template selected" in the Select.
const WHATSAPP_TEMPLATE_NONE_SENTINEL = '__none__';

// Fields on the `params` object must match HubspotIntegrationParams in
// backend/appquickchat/appquickchat/api/integrations/integration_params_schemas.py.
export interface HubspotIntegrationParams {
  hubspot_bot_active: boolean;
  hubspot_code: string;
  hubspot_bot_email: string;
  hubspot_bot_email_new: string;
}

export interface HubspotIntegrationResponse {
  name: 'hubspot';
  params: HubspotIntegrationParams;
}

interface HubspotOauthUrlResponse {
  url: string;
}

interface HubspotTokenStatusResponse {
  has_token: boolean;
  is_full_integration_active: boolean;
}

interface HubspotPipeline {
  id: number;
  label: string;
}

interface HubspotPipelinesResponse {
  pipelines: HubspotPipeline[];
  working_hours_pipeline_id: number;
  outside_working_hours_pipeline_id: number;
}

interface HubspotWhatsappTemplateConfig {
  template_name: string;
  template_language: string;
}

interface WhatsAppTemplateEntry {
  id?: string;
  name: string;
  language: string;
  status?: string;
  category?: string;
}

interface WhatsAppTemplatesResponse {
  templates: WhatsAppTemplateEntry[];
}

interface AutoCloseResponse {
  auto_close_minutes: AutoCloseValue;
  valid_values: AutoCloseValue[];
}

const emailSchema = z.object({
  hubspot_bot_email: z.string().email(),
});
type EmailFormValues = z.infer<typeof emailSchema>;

const connectedFormSchema = z.object({
  hubspot_bot_active: z.boolean(),
  hubspot_bot_email_new: z.string().email().or(z.literal('')),
  working_hours_pipeline_id: z.string(),
  outside_working_hours_pipeline_id: z.string(),
  auto_close: z.string(),
  whatsapp_template: z.string(),
});
type ConnectedFormValues = z.infer<typeof connectedFormSchema>;

const autoCloseOptionKey = (value: AutoCloseValue): string =>
  value === null ? AUTO_CLOSE_NEVER_SENTINEL : String(value);

const parseAutoCloseKey = (key: string): AutoCloseValue => {
  if (key === AUTO_CLOSE_NEVER_SENTINEL) return null;
  const num = Number(key);
  return (AUTO_CLOSE_VALUES as readonly AutoCloseValue[]).includes(num as AutoCloseValue)
    ? (num as AutoCloseValue)
    : null;
};

const whatsappTemplateKey = (name: string, language: string): string => {
  if (!name) return WHATSAPP_TEMPLATE_NONE_SENTINEL;
  return `${name}|${language || ''}`;
};

const parseWhatsappTemplateKey = (
  key: string,
): { template_name: string; template_language: string } => {
  if (!key || key === WHATSAPP_TEMPLATE_NONE_SENTINEL) {
    return { template_name: '', template_language: '' };
  }
  const sepIndex = key.indexOf('|');
  if (sepIndex === -1) return { template_name: key, template_language: '' };
  return {
    template_name: key.slice(0, sepIndex),
    template_language: key.slice(sepIndex + 1),
  };
};

export interface HubspotSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const HubspotSheet = ({ open, onOpenChange }: HubspotSheetProps) => {
  const { t } = useTranslation(['integration']);
  const queryClient = useQueryClient();
  const popupPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const popupRef = useRef<Window | null>(null);

  const clearPopupPoll = useCallback(() => {
    if (popupPollRef.current !== null) {
      clearInterval(popupPollRef.current);
      popupPollRef.current = null;
    }
  }, []);

  // Cleanup the popup poll on unmount.
  useEffect(
    () => () => {
      clearPopupPoll();
    },
    [clearPopupPoll],
  );

  const integrationQuery = useQuery<HubspotIntegrationResponse>({
    queryKey: HUBSPOT_QUERY_KEY,
    enabled: open,
  });

  const params = integrationQuery.data?.params;
  const isConnected = Boolean(params?.hubspot_code);

  const tokenStatusQuery = useQuery<HubspotTokenStatusResponse>({
    queryKey: HUBSPOT_TOKEN_STATUS_QUERY_KEY,
    enabled: open,
  });

  const pipelinesQuery = useQuery<HubspotPipelinesResponse>({
    queryKey: HUBSPOT_PIPELINES_QUERY_KEY,
    enabled: open && isConnected,
  });

  const autoCloseQuery = useQuery<AutoCloseResponse>({
    queryKey: AUTO_CLOSE_QUERY_KEY,
    enabled: open && isConnected,
  });

  const whatsappTemplateConfigQuery = useQuery<HubspotWhatsappTemplateConfig>({
    queryKey: HUBSPOT_WHATSAPP_TEMPLATE_QUERY_KEY,
    enabled: open && isConnected,
  });

  // Disabled retry so that a non-200 response (e.g. WhatsApp not configured)
  // surfaces as an error and we can hide the WhatsApp section.
  const whatsappTemplatesQuery = useQuery<WhatsAppTemplatesResponse>({
    queryKey: HUBSPOT_WHATSAPP_TEMPLATES_LIST_QUERY_KEY,
    enabled: open && isConnected,
    retry: false,
  });

  const whatsappTemplates = whatsappTemplatesQuery.data?.templates ?? [];
  const showWhatsappSection =
    isConnected && !whatsappTemplatesQuery.isError && whatsappTemplates.length > 0;

  const patchIntegrationMutation = useMutation<
    unknown,
    Error,
    {
      name: typeof HUBSPOT_INTEGRATION_NAME;
      params: {
        hubspot_bot_active: boolean;
        hubspot_bot_email?: string;
        hubspot_bot_email_new?: string;
      };
    }
  >({
    mutationKey: [QueryKey.integration.patch],
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: HUBSPOT_QUERY_KEY });
      toast.success(t(`${HUBSPOT_NS_PREFIX}.saved`));
    },
    onError: () => {
      toast.error(t(`${HUBSPOT_NS_PREFIX}.save_failed`));
    },
  });

  const patchPipelinesMutation = useMutation<
    unknown,
    Error,
    { working_hours_pipeline_id: number; outside_working_hours_pipeline_id: number }
  >({
    mutationKey: [QueryKey.integration.hubspotPipelinesPatch],
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: HUBSPOT_PIPELINES_QUERY_KEY });
      toast.success(t(`${HUBSPOT_NS_PREFIX}.saved`));
    },
    onError: () => {
      toast.error(t(`${HUBSPOT_NS_PREFIX}.pipelines.save_failed`));
    },
  });

  const patchAutoCloseMutation = useMutation<unknown, Error, AutoCloseValue>({
    mutationKey: [QueryKey.integration.autoCloseMinutesPatch],
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: AUTO_CLOSE_QUERY_KEY });
    },
    onError: () => {
      toast.error(t(`${HUBSPOT_NS_PREFIX}.save_failed`));
    },
  });

  const patchWhatsappTemplateMutation = useMutation<
    unknown,
    Error,
    { template_name: string; template_language: string }
  >({
    mutationKey: [QueryKey.integration.hubspotWhatsappTemplatePatch],
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: HUBSPOT_WHATSAPP_TEMPLATE_QUERY_KEY });
    },
    onError: () => {
      toast.error(t(`${HUBSPOT_NS_PREFIX}.whatsapp.save_failed`));
    },
  });

  // The popup is opened synchronously in the click/submit handler so Safari
  // keeps it tied to the user gesture; this mutation fetches the OAuth URL and
  // points the already-open popup at it. We then poll the popup for `closed` to
  // refetch the integration + token-status queries when the user finishes (or
  // cancels) the flow.
  const oauthMutation = useMutation<HubspotOauthUrlResponse>({
    mutationKey: [QueryKey.integration.hubspotOauthMutation],
    onSuccess: (data) => {
      const popup = popupRef.current;
      if (!popup || popup.closed) {
        toast.error(t(`${HUBSPOT_NS_PREFIX}.popup_blocked`));
        return;
      }
      popup.location.href = data.url;

      clearPopupPoll();
      popupPollRef.current = setInterval(() => {
        // Guard against the host going away mid-poll (test teardown, page nav).
        if (typeof window === 'undefined' || !popup) {
          clearPopupPoll();
          return;
        }
        if (popup.closed) {
          clearPopupPoll();
          popupRef.current = null;
          queryClient.invalidateQueries({ queryKey: HUBSPOT_QUERY_KEY });
          queryClient.invalidateQueries({ queryKey: HUBSPOT_TOKEN_STATUS_QUERY_KEY });
          queryClient.invalidateQueries({ queryKey: HUBSPOT_PIPELINES_QUERY_KEY });
        }
      }, POPUP_POLL_INTERVAL_MS);
    },
    onError: () => {
      popupRef.current?.close();
      popupRef.current = null;
      toast.error(t(`${HUBSPOT_NS_PREFIX}.oauth_failed`));
    },
  });

  // --- Not-connected form (email only) ---
  const emailForm = useForm<EmailFormValues>({
    mode: 'onChange',
    resolver: zodResolver(emailSchema),
    defaultValues: { hubspot_bot_email: '' },
  });

  useEffect(() => {
    if (!isConnected && params?.hubspot_bot_email) {
      emailForm.reset({ hubspot_bot_email: params.hubspot_bot_email });
    }
  }, [params, isConnected, emailForm]);

  const handleStartOauth = emailForm.handleSubmit(async (values) => {
    // Open the popup synchronously so Safari keeps it bound to the click; its
    // location is set once oauthMutation resolves with the OAuth URL.
    popupRef.current = window.open('', 'hubspot_oauth', 'width=600,height=700');
    if (!popupRef.current) {
      toast.error(t(`${HUBSPOT_NS_PREFIX}.popup_blocked`));
      return;
    }
    try {
      // Persist email before launching OAuth so the callback has it.
      await patchIntegrationMutation.mutateAsync({
        name: HUBSPOT_INTEGRATION_NAME,
        params: {
          hubspot_bot_active: params?.hubspot_bot_active ?? false,
          hubspot_bot_email: values.hubspot_bot_email,
        },
      });
    } catch {
      popupRef.current?.close();
      popupRef.current = null;
      return;
    }
    oauthMutation.mutate();
  });

  // --- Connected form ---
  const defaultConnectedValues: ConnectedFormValues = useMemo(
    () => ({
      hubspot_bot_active: params?.hubspot_bot_active ?? false,
      hubspot_bot_email_new: '',
      working_hours_pipeline_id: pipelinesQuery.data?.working_hours_pipeline_id
        ? String(pipelinesQuery.data.working_hours_pipeline_id)
        : '',
      outside_working_hours_pipeline_id: pipelinesQuery.data?.outside_working_hours_pipeline_id
        ? String(pipelinesQuery.data.outside_working_hours_pipeline_id)
        : '',
      auto_close: autoCloseOptionKey(autoCloseQuery.data?.auto_close_minutes ?? null),
      whatsapp_template: whatsappTemplateKey(
        whatsappTemplateConfigQuery.data?.template_name ?? '',
        whatsappTemplateConfigQuery.data?.template_language ?? '',
      ),
    }),
    [params, pipelinesQuery.data, autoCloseQuery.data, whatsappTemplateConfigQuery.data],
  );

  const connectedForm = useForm<ConnectedFormValues>({
    mode: 'onChange',
    resolver: zodResolver(connectedFormSchema),
    defaultValues: defaultConnectedValues,
  });

  useEffect(() => {
    connectedForm.reset(defaultConnectedValues);
  }, [defaultConnectedValues, connectedForm]);

  const handleConnectedSubmit = connectedForm.handleSubmit((values) => {
    // Patch the core integration (toggle + optional email override).
    const integrationPayload: {
      hubspot_bot_active: boolean;
      hubspot_bot_email?: string;
      hubspot_bot_email_new?: string;
    } = {
      hubspot_bot_active: values.hubspot_bot_active,
      hubspot_bot_email: params?.hubspot_bot_email ?? '',
    };
    if (
      values.hubspot_bot_email_new &&
      values.hubspot_bot_email_new !== params?.hubspot_bot_email
    ) {
      integrationPayload.hubspot_bot_email_new = values.hubspot_bot_email_new;
    }
    patchIntegrationMutation.mutate({
      name: HUBSPOT_INTEGRATION_NAME,
      params: integrationPayload,
    });

    // Patch pipelines when both values are set.
    if (values.working_hours_pipeline_id && values.outside_working_hours_pipeline_id) {
      patchPipelinesMutation.mutate({
        working_hours_pipeline_id: Number(values.working_hours_pipeline_id),
        outside_working_hours_pipeline_id: Number(values.outside_working_hours_pipeline_id),
      });
    }

    // Patch auto-close if it changed.
    const nextAutoClose = parseAutoCloseKey(values.auto_close);
    if (nextAutoClose !== (autoCloseQuery.data?.auto_close_minutes ?? null)) {
      patchAutoCloseMutation.mutate(nextAutoClose);
    }

    // Patch WhatsApp template if the section is visible.
    if (showWhatsappSection) {
      patchWhatsappTemplateMutation.mutate(parseWhatsappTemplateKey(values.whatsapp_template));
    }
  });

  // Individual pipeline-change handler so tests can observe an immediate PATCH.
  const handlePipelineChange = useCallback(
    (field: 'working_hours_pipeline_id' | 'outside_working_hours_pipeline_id', value: string) => {
      const working =
        field === 'working_hours_pipeline_id'
          ? value
          : connectedForm.getValues('working_hours_pipeline_id');
      const outside =
        field === 'outside_working_hours_pipeline_id'
          ? value
          : connectedForm.getValues('outside_working_hours_pipeline_id');
      if (!working || !outside) return;
      patchPipelinesMutation.mutate({
        working_hours_pipeline_id: Number(working),
        outside_working_hours_pipeline_id: Number(outside),
      });
    },
    [connectedForm, patchPipelinesMutation],
  );

  // Individual auto-close handler.
  const handleAutoCloseChange = useCallback(
    (key: string) => {
      connectedForm.setValue('auto_close', key);
      patchAutoCloseMutation.mutate(parseAutoCloseKey(key));
    },
    [connectedForm, patchAutoCloseMutation],
  );

  const isInitialLoading = integrationQuery.isLoading;
  const hasToken = Boolean(tokenStatusQuery.data?.has_token) || isConnected;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        className="max-w-3xl w-full h-full"
        tabIndex={undefined}
        data-testid="hubspot-sheet"
      >
        <SheetHeader className="px-0.5">
          <SheetTitle className="flex items-center gap-2" data-testid="hubspot-sheet-title">
            <HubSpotIcon className="size-5" />
            {t(`${HUBSPOT_NS_PREFIX}.title`)}
          </SheetTitle>
          <IconButton
            onClick={() => onOpenChange(false)}
            variant="secondary"
            data-testid="hubspot-close-button"
          >
            <CloseIcon />
          </IconButton>
        </SheetHeader>

        <p className="text-sm text-muted-foreground px-0.5" data-testid="hubspot-description">
          {t(`${HUBSPOT_NS_PREFIX}.description`)}
        </p>

        <div className="overflow-y-auto px-0.5 space-y-6 flex-1">
          {isInitialLoading ? (
            <div className="flex items-center justify-center py-12" data-testid="hubspot-loading">
              <Loader2 className="size-6 animate-spin text-foreground-subtle" />
            </div>
          ) : !isConnected ? (
            <form
              onSubmit={handleStartOauth}
              className="flex flex-col items-stretch gap-4 py-6"
              data-testid="hubspot-not-connected"
            >
              <div className="flex flex-col items-center gap-3 py-4 border border-dashed border-border rounded-xl">
                <Plug className="size-10 text-foreground-subtle" />
                <p className="text-sm text-muted-foreground text-center max-w-sm">
                  {t(`${HUBSPOT_NS_PREFIX}.description`)}
                </p>
              </div>

              <div className="space-y-2">
                <Label
                  htmlFor="hubspot-email-input"
                  className="text-sm font-medium text-foreground"
                >
                  {t(`${HUBSPOT_NS_PREFIX}.email.label`)}
                </Label>
                <Input
                  id="hubspot-email-input"
                  type="email"
                  size="sm"
                  placeholder={t(`${HUBSPOT_NS_PREFIX}.email.placeholder`)}
                  error={Boolean(emailForm.formState.errors.hubspot_bot_email)}
                  data-testid="hubspot-email-input"
                  {...emailForm.register('hubspot_bot_email')}
                />
                {emailForm.formState.errors.hubspot_bot_email && (
                  <p className="text-xs text-destructive" data-testid="hubspot-email-error">
                    {t(`${HUBSPOT_NS_PREFIX}.email.required`)}
                  </p>
                )}
              </div>

              <Button
                type="submit"
                variant="dark"
                disabled={
                  !emailForm.formState.isValid ||
                  patchIntegrationMutation.isPending ||
                  oauthMutation.isPending
                }
                data-testid="hubspot-authenticate-button"
              >
                {patchIntegrationMutation.isPending || oauthMutation.isPending ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <ExternalLink className="size-4" />
                )}
                {t(`${HUBSPOT_NS_PREFIX}.authenticate`)}
              </Button>
            </form>
          ) : (
            <form onSubmit={handleConnectedSubmit} className="space-y-6" data-testid="hubspot-form">
              <div
                className="rounded-xl border border-success-muted bg-success-muted p-4 space-y-2"
                data-testid="hubspot-connection-card"
              >
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="size-5 text-success" />
                  <span
                    className="text-sm font-medium text-success"
                    data-testid="hubspot-connected-as"
                  >
                    {t(`${HUBSPOT_NS_PREFIX}.connected_as`, {
                      email: params?.hubspot_bot_email || '',
                    })}
                  </span>
                </div>
                <div className="pt-2">
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={() => {
                      // Open the popup synchronously (Safari gesture rule); its URL is set in onSuccess.
                      popupRef.current = window.open('', 'hubspot_oauth', 'width=600,height=700');
                      if (!popupRef.current) {
                        toast.error(t(`${HUBSPOT_NS_PREFIX}.popup_blocked`));
                        return;
                      }
                      oauthMutation.mutate();
                    }}
                    disabled={oauthMutation.isPending}
                    data-testid="hubspot-reauthenticate-button"
                  >
                    <RefreshCw className="size-4" />
                    {t(`${HUBSPOT_NS_PREFIX}.reauthenticate`)}
                  </Button>
                </div>
                {!hasToken && (
                  <p
                    className="text-xs text-warning-foreground"
                    data-testid="hubspot-no-token-warning"
                  >
                    {t(`${HUBSPOT_NS_PREFIX}.pipelines.load_failed`)}
                  </p>
                )}
              </div>

              <Separator />

              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <Label className="text-sm font-medium text-foreground">
                    {t(`${HUBSPOT_NS_PREFIX}.enable.label`)}
                  </Label>
                </div>
                <Controller
                  control={connectedForm.control}
                  name="hubspot_bot_active"
                  render={({ field }) => (
                    <Switch
                      checked={field.value}
                      onCheckedChange={(checked) => {
                        field.onChange(checked);
                        patchIntegrationMutation.mutate({
                          name: HUBSPOT_INTEGRATION_NAME,
                          params: {
                            hubspot_bot_active: checked,
                            hubspot_bot_email: params?.hubspot_bot_email ?? '',
                          },
                        });
                      }}
                      data-testid="hubspot-active-toggle"
                    />
                  )}
                />
              </div>

              {pipelinesQuery.data && pipelinesQuery.data.pipelines.length > 0 && (
                <>
                  <Separator />
                  <div className="space-y-4" data-testid="hubspot-pipelines-section">
                    <div>
                      <h3 className="text-sm font-semibold text-foreground">
                        {t(`${HUBSPOT_NS_PREFIX}.pipelines.heading`)}
                      </h3>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-sm font-medium text-foreground">
                        {t(`${HUBSPOT_NS_PREFIX}.pipelines.working_hours`)}
                      </Label>
                      <Controller
                        control={connectedForm.control}
                        name="working_hours_pipeline_id"
                        render={({ field }) => (
                          <Select
                            value={field.value}
                            onValueChange={(value) => {
                              field.onChange(value);
                              handlePipelineChange('working_hours_pipeline_id', value);
                            }}
                          >
                            <SelectTrigger
                              className="w-full"
                              data-testid="hubspot-working-hours-pipeline-select"
                            >
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {pipelinesQuery.data?.pipelines.map((p) => (
                                <SelectItem
                                  key={p.id}
                                  value={String(p.id)}
                                  data-testid={`hubspot-working-hours-pipeline-option-${p.id}`}
                                >
                                  {p.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label className="text-sm font-medium text-foreground">
                        {t(`${HUBSPOT_NS_PREFIX}.pipelines.outside_hours`)}
                      </Label>
                      <Controller
                        control={connectedForm.control}
                        name="outside_working_hours_pipeline_id"
                        render={({ field }) => (
                          <Select
                            value={field.value}
                            onValueChange={(value) => {
                              field.onChange(value);
                              handlePipelineChange('outside_working_hours_pipeline_id', value);
                            }}
                          >
                            <SelectTrigger
                              className="w-full"
                              data-testid="hubspot-outside-hours-pipeline-select"
                            >
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {pipelinesQuery.data?.pipelines.map((p) => (
                                <SelectItem
                                  key={p.id}
                                  value={String(p.id)}
                                  data-testid={`hubspot-outside-hours-pipeline-option-${p.id}`}
                                >
                                  {p.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      />
                    </div>
                  </div>
                </>
              )}

              <Separator />

              <div className="space-y-2">
                <Label className="text-sm font-medium text-foreground">
                  {t(`${HUBSPOT_NS_PREFIX}.auto_close.label`)}
                </Label>
                <Controller
                  control={connectedForm.control}
                  name="auto_close"
                  render={({ field }) => (
                    <Select
                      value={field.value}
                      onValueChange={(value) => {
                        field.onChange(value);
                        handleAutoCloseChange(value);
                      }}
                    >
                      <SelectTrigger className="w-full" data-testid="hubspot-auto-close-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem
                          value={AUTO_CLOSE_NEVER_SENTINEL}
                          data-testid="hubspot-auto-close-option-never"
                        >
                          {t(`${HUBSPOT_NS_PREFIX}.auto_close.options.never`)}
                        </SelectItem>
                        <SelectItem value="5" data-testid="hubspot-auto-close-option-5">
                          {t(`${HUBSPOT_NS_PREFIX}.auto_close.options.5`)}
                        </SelectItem>
                        <SelectItem value="10" data-testid="hubspot-auto-close-option-10">
                          {t(`${HUBSPOT_NS_PREFIX}.auto_close.options.10`)}
                        </SelectItem>
                        <SelectItem value="30" data-testid="hubspot-auto-close-option-30">
                          {t(`${HUBSPOT_NS_PREFIX}.auto_close.options.30`)}
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>

              {showWhatsappSection && (
                <>
                  <Separator />
                  <div className="space-y-3" data-testid="hubspot-whatsapp-section">
                    <div>
                      <h3 className="text-sm font-semibold text-foreground">
                        {t(`${HUBSPOT_NS_PREFIX}.whatsapp.heading`)}
                      </h3>
                      <p className="text-xs text-muted-foreground mt-1">
                        {t(`${HUBSPOT_NS_PREFIX}.whatsapp.description`)}
                      </p>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm font-medium text-foreground">
                        {t(`${HUBSPOT_NS_PREFIX}.whatsapp.template`)}
                      </Label>
                      <Controller
                        control={connectedForm.control}
                        name="whatsapp_template"
                        render={({ field }) => (
                          <Select
                            value={field.value || WHATSAPP_TEMPLATE_NONE_SENTINEL}
                            onValueChange={(value) => {
                              field.onChange(value);
                              patchWhatsappTemplateMutation.mutate(parseWhatsappTemplateKey(value));
                            }}
                          >
                            <SelectTrigger
                              className="w-full"
                              data-testid="hubspot-whatsapp-template-select"
                            >
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem
                                value={WHATSAPP_TEMPLATE_NONE_SENTINEL}
                                data-testid="hubspot-whatsapp-template-option-none"
                              >
                                {t(`${HUBSPOT_NS_PREFIX}.whatsapp.none`)}
                              </SelectItem>
                              {whatsappTemplates.map((tpl) => (
                                <SelectItem
                                  key={`${tpl.name}-${tpl.language}`}
                                  value={whatsappTemplateKey(tpl.name, tpl.language)}
                                  data-testid={`hubspot-whatsapp-template-option-${tpl.name}-${tpl.language}`}
                                >
                                  {tpl.name} ({tpl.language})
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      />
                    </div>
                  </div>
                </>
              )}

              <div className="pt-2">
                <Button
                  type="submit"
                  variant="dark"
                  disabled={patchIntegrationMutation.isPending}
                  data-testid="hubspot-save-button"
                >
                  {patchIntegrationMutation.isPending && (
                    <Loader2 className="size-4 animate-spin" />
                  )}
                  {t(`${HUBSPOT_NS_PREFIX}.save`)}
                </Button>
              </div>
            </form>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};
